// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddUserVisaDialog from './AddUserVisaDialog';
import Autocomplete from '@mui/material/Autocomplete';
import BackdropProgress from '../../../BackdropProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CustomSnackbar from '../../ui/Snackbars';
import EnhancedTableHead from '../../ui/EnhancedTableHead.js';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import SearchIcon from "@mui/icons-material/Search";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import useHttp from '../../../utils/http';
import UserVisaInfoCard from './UserVisaInfoCard';
import wso2Theme from '../../../theme';
import { adjustForTimezone, getCountryNameFromCountryCode, getDateForDB, getCountryList, getTimeLabelAndColor, truncateString, getComparator, stableSort } from '../../../utils/formatting';
import { isAccessGranted } from "../../../utils/oauth";
import { postSearchOwnVisas } from '../../../utils/end-points.js';
import { PRIVILEGES } from "../../../Config.js";
import { NO_VALID_VISA_AVAILABLE_ID, VISA_NOT_APPLICABLE_ID } from '../../../Config';
import { useEffect, useState, useCallback } from 'react';
import { visaStatus, visaStatusArray } from '../../../utils/const';
import { getVisaStatusColor } from "../../../theme/status-colors"
import "../../../App.css";
import "../../../css/UserVisaList.scss"


const headCells = [
    { id: 'country', label: 'Country', align: 'left', disablePadding: false, sortable: true },
    { id: 'visa_type', label: 'Visa Type', align: 'left', disablePadding: false, sortable: true },
    { id: 'entry_type', label: 'Entry Type', align: 'left', disablePadding: false, sortable: true },
    { id: 'start_date', label: 'Start Date', align: 'left', disablePadding: false, sortable: true },
    { id: 'end_date', label: 'End Date', align: 'left', disablePadding: false, sortable: true },
    { id: 'daysToExpire', label: 'Expires In', align: 'center', disablePadding: false, sortable: false },
];

const UserVisaList = () => {

    const { handleRequest } = useHttp()
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [order, setOrder] = useState('asc')
    const [orderBy, setOrderBy] = useState('end_date')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(8)
    const [visaList, setVisaList] = useState([])
    const [selectedVisa, setSelectedVisa] = useState(null)
    const [isPageLoading, setPageLoading] = useState(true)
    const [isTableLoading, setTableLoading] = useState(false)
    const [isTablePaginating, setTablePaginating] = useState(false)
    const [addVisaDialog, setAddVisaDialog] = useState(false)
    const [countryFilter, setCountryFilter] = useState(null)
    const [statusFilter, setStatusFilter] = useState("Active")
    const [searchFilter, setSearchFilter] = useState("")
    const [snackbarData, setSnackbarData] = useState({ open: false, message: "", severity: "", onClose: null })

    useEffect(() => {
        fetchVisaList(rowsPerPage, "", "", "Active", "end_date", "asc")

        if (window.innerHeight >= 850) {
            setRowsPerPage(13)
        }

    }, [])

    useEffect(() => {
        fetchVisaList(rowsPerPage, searchFilter, countryFilter, statusFilter, orderBy, order)
    }, [countryFilter, statusFilter, orderBy, order])

    const fetchVisaList = useCallback(async (limit, searchFilter, country, status, sortColumn, sortOrder, showSuccessSnack) => {
        setTableLoading(true)
        let body = {
            status: "active",
            limit: limit,
            offset: 0,
            searchString: searchFilter,
            country: country?.countryCode,
            minEndDate: status === "Active" ? new Date().toISOString().slice(0, 10) : null,
            maxEndDate: status === "Expired" ? new Date().toISOString().slice(0, 10) : null,
            sortColumn: sortColumn,
            sortOrder: sortOrder
        }

        handleRequest(postSearchOwnVisas, "POST", body, (data) => {
            if (data) {
                setVisaList(data)
                if (data.length > 0) {
                    setSelectedVisa(data[0])
                }
                else {
                    setSelectedVisa(null)
                }
                if (showSuccessSnack) {
                    setSnackbarData({ message: "Success", open: true, severity: "success", onClose: onCloseSnackbar })
                }
            }
            else {
                setSnackbarData({ message: "Error while fetching visas", open: true, severity: "error", onClose: onCloseSnackbar })
            }
            setTableLoading(false)
            setPageLoading(false)
        }, (error) => {
            setTableLoading(false)
            setPageLoading(false)
            console.error(error)
            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
        })

    }, [])


    const loadMoreVisas = (limit, offset, searchFilter, country, status, sortColumn, sortOrder) => {
        setTablePaginating(true)
        let body = {
            status: "active",
            limit: limit,
            offset: offset,
            searchString: searchFilter,
            country: country?.countryCode,
            minEndDate: status === "Active" ? new Date().toISOString().slice(0, 10) : null,
            maxEndDate: status === "Expired" ? new Date().toISOString().slice(0, 10) : null,
            sortColumn: sortColumn,
            sortOrder: sortOrder
        }

        handleRequest(postSearchOwnVisas, "POST", body, (visas) => {
            if (visas) {
                let filteredVisa = visas.filter(visa => (visa.id !== NO_VALID_VISA_AVAILABLE_ID.id && visa.id !== VISA_NOT_APPLICABLE_ID.id))
                let tempVisaList = visaList.concat(filteredVisa)
                setVisaList(tempVisaList)
                if (visas.length === 0) {
                    setPage(page => page - 1)
                    setSnackbarData({ message: "No more data", open: true, severity: "success", onClose: onCloseSnackbar })
                }
            }
            else {
                setSnackbarData({ message: "Error while fetching visas", open: true, severity: "error", onClose: onCloseSnackbar })
            }
            setTablePaginating(false)
            setPageLoading(false)
        }, (error) => {
            setTablePaginating(false)
            setPageLoading(false)
            console.error(error)
            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
        })
    }

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        fetchVisaList(parseInt(event.target.value, 10), searchFilter, countryFilter, statusFilter, orderBy, order)
        setPage(0);
    }

    const handleChangePage = (event, newPage) => {
        if (visaList.length <= newPage * rowsPerPage) {
            loadMoreVisas(rowsPerPage, visaList.length, searchFilter, countryFilter, statusFilter, orderBy, order)
        }
        setPage(newPage);
    }

    const handleClick = (visa) => {
        setSelectedVisa(visa)
    }

    const handleCountryFilter = (event, value) => {
        setCountryFilter(value)
    }

    const handleStatusFilter = (event, value) => {
        setStatusFilter(value)
    }

    const handleSearchFilter = (event) => {
        setSearchFilter(event.target.value)
        if (!event.target.value) {
            fetchVisaList(rowsPerPage, event.target.value, countryFilter, statusFilter, orderBy, order)
        }
    }

    const searchTable = () => {
        fetchVisaList(rowsPerPage, searchFilter, countryFilter, statusFilter, orderBy, order)
    }

    const getPassportTaggedLabel = (passportId) => {

        return <span className='Status-chip'
            style={{
                backgroundColor: passportId ? wso2Theme.palette.status.completed : wso2Theme.palette.status.expired,
                color: "white"
            }}>
            {passportId ? "Yes" : "No"}
        </span>
    }

    const getTimeToExpire = (endDate) => {

        var visaEndDate = adjustForTimezone(new Date(endDate))
        var currentDate = new Date()

        return <span className='Status-chip'
            style={{
                backgroundColor: (getDateForDB(currentDate) <= getDateForDB(visaEndDate)) ? getTimeLabelAndColor(visaEndDate, currentDate)[1] : "#F37878",
                color: "white"
            }}>
            {(getDateForDB(currentDate) <= getDateForDB(visaEndDate)) ? getTimeLabelAndColor(visaEndDate, currentDate)[0] : "Expired"}
        </span>
    }

    const handleCloseAddVisaDialog = () => {
        setAddVisaDialog(false)
    }

    const onCloseSnackbar = () => {
        setSnackbarData({ ...snackbarData, open: false })
    }

    return (
        <div className='User-visa-list'>
            <CustomSnackbar snackbar={snackbarData} />

            <div className='Page-title'>My Visa Details</div>

            <BackdropProgress open={isPageLoading} />

            {!isPageLoading && (
                <div>
                    <AddUserVisaDialog open={addVisaDialog} onClose={handleCloseAddVisaDialog} refreshVisaList={() => fetchVisaList(rowsPerPage, searchFilter, countryFilter, statusFilter, orderBy, order, true)} visaList={visaList} />

                    <Grid container>
                        <Grid item lg={8} md={7} xs={12} className='Table-grid'>

                            <Paper className='Border-elevation'>
                                <div className='Table-actions-div' style={{ borderColor: wso2Theme.palette.primary.light }}>
                                    <div className="Table-actions">

                                        <TextField variant="outlined" label="Search" size="small"
                                            className='Search-text-field'
                                            onChange={handleSearchFilter} />
                                        <Button variant={"contained"} startIcon={<SearchIcon />} color="secondary" className='Filter-button'
                                            onClick={searchTable} >Search</Button>
                                        <Button variant={filtersExpanded ? "contained" : "outlined"} startIcon={<FilterAltIcon />} color="primary" className='Filter-button'
                                            onClick={() => setFiltersExpanded(!filtersExpanded)} >{filtersExpanded ? "Hide Filters" : "Show Filters"}</Button>
                                        {isAccessGranted([PRIVILEGES.ADD_OWN_VISA]) && <Button variant={"contained"} startIcon={<AddCircleOutlineIcon />} color="secondary" className='Add-visa-button'
                                            onClick={() => setAddVisaDialog(true)} >Add Visa</Button>}

                                    </div>
                                    {filtersExpanded && (
                                        <div className='Filters-div'>
                                            <Grid container>
                                                <Grid item lg={4} md={6} className='Filter-grid-item-country'>
                                                    <Autocomplete
                                                        size="small"
                                                        disablePortal
                                                        value={countryFilter}
                                                        options={getCountryList()}
                                                        getOptionLabel={(option) => option.country}
                                                        onChange={handleCountryFilter}
                                                        renderOption={(props, option) => (
                                                            <Box {...props}>{option.country}</Box>
                                                        )}
                                                        renderInput={(params) => <TextField  {...params} label="Country" />}
                                                    />
                                                </Grid>
                                                <Grid item lg={4} md={6} className='Filter-grid-item-status'>
                                                    <Autocomplete
                                                        size="small"
                                                        disablePortal
                                                        options={visaStatusArray.filter(status => status !== visaStatus.INACTIVE)}
                                                        getOptionLabel={(option) => option}
                                                        defaultValue={statusFilter}
                                                        onChange={handleStatusFilter}
                                                        renderOption={(props, option) => (
                                                            <Box {...props} >
                                                                <span className="Status-dot-dropdown" style={{ backgroundColor: (option == visaStatus.ACTIVE) ? getVisaStatusColor(true) : getVisaStatusColor(false) }}></span>
                                                                {option}
                                                            </Box>
                                                        )}
                                                        renderInput={(params) => <TextField  {...params} label="Status" />}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </div>
                                    )}
                                </div>

                                {isTableLoading && <LinearProgress color='secondary' />}

                                <TableContainer>

                                    <Table size={'small'} >
                                        <EnhancedTableHead headCells={headCells} order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                                        <TableBody>
                                            {selectedVisa && stableSort(visaList, getComparator(order, orderBy))
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((row) => {
                                                    return (
                                                        <TableRow onClick={(event) => handleClick(row)} tabIndex={-1} key={row.id}
                                                            className={selectedVisa.id === row.id ? 'Selected-row' : "Table-row"}
                                                            style={{
                                                                backgroundColor: selectedVisa.id === row.id ? "white" : null,
                                                                elevation: selectedVisa.id === row.id ? "above" : null,
                                                            }}>
                                                            <TableCell className='Table-cell'>{truncateString(getCountryNameFromCountryCode(row.country) ? getCountryNameFromCountryCode(row.country) : row.country, 20)}</TableCell>
                                                            <TableCell className='Table-cell'>{truncateString(row.visaType, 20)}</TableCell>
                                                            <TableCell className='Table-cell'>{truncateString(row.entryType, 20)}</TableCell>
                                                            <TableCell className='Table-cell'>{getDateForDB(adjustForTimezone(new Date(row.startDate)))}</TableCell>
                                                            <TableCell className='Table-cell'>{getDateForDB(adjustForTimezone(new Date(row.endDate)))}</TableCell>
                                                            {/* <TableCell className='Table-cell Table-cell-passport'>{getPassportTaggedLabel(row.passportId)}</TableCell> */}
                                                            <TableCell className='Table-cell Table-cell-days'>{getTimeToExpire(row.endDate)}</TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                        </TableBody>
                                    </Table>
                                    {!isPageLoading && visaList.length === 0 && <div className='No-visas-label'>No Visas</div>}
                                </TableContainer>
                                {isTablePaginating && <LinearProgress color='secondary' />}
                                <TablePagination
                                    rowsPerPageOptions={window.innerHeight >= 850 ? [5, 13, 25] : [5, 10, 25]}
                                    component="div"
                                    count={-1}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </Paper>
                        </Grid>{selectedVisa ?
                            <Grid item lg={4} md={5} xs={12} className='Details-grid'>
                                <UserVisaInfoCard selectedVisa={selectedVisa} refreshVisaList={() => fetchVisaList(rowsPerPage, searchFilter, countryFilter, statusFilter, orderBy, order, true)} />
                            </Grid> : null}
                    </Grid>
                </div>
            )}

        </div>
    )

}

export default UserVisaList;